/**
 * Canonical permission hook — UI permission gates (B-1.B.10).
 *
 * Single entry point for "is this user allowed to do X?" checks in the
 * client. Mirrors the FLAG lane's `useFeatureFlags` pattern: every UI
 * conditional render that should be permission-gated routes through
 * this hook so we have one place to evolve the authorization layer
 * (e.g. swapping from session-bound permissions to per-tenant policy
 * resolution) without sweeping the codebase again.
 *
 * The hook delegates to the auth context's `hasPermission`, which is
 * itself driven by either the server-resolved session permission set
 * or — as a fallback for legacy responses — the canonical
 * `ROLE_PERMISSIONS` registry from `shared/permissions.ts`.
 *
 * Usage:
 *   const allowed = usePermission("ptw.approve");
 *   if (!allowed) return null;
 *
 *   const { hasPermission, hasAll, hasAny } = usePermissions();
 *   if (hasAny(["incident.investigate", "incident.close"])) { ... }
 */
import { useMemo } from "react";
import { useAuth } from "@/lib/auth-context";
import type { Permission } from "@shared/permissions";

export type { Permission } from "@shared/permissions";

/**
 * Returns whether the active user has the given permission in the
 * currently-active tenant context. Returns `false` when no user is
 * loaded (fail-closed) so callers don't have to special-case the
 * unauthenticated state.
 */
export function usePermission(permission: Permission | null | undefined): boolean {
  const { hasPermission } = useAuth();
  if (!permission) return false;
  return hasPermission(permission as Permission);
}

export interface UsePermissionsApi {
  /** Single-permission check. */
  hasPermission: (permission: Permission) => boolean;
  /** Returns true only if every permission in the list is granted. */
  hasAll: (permissions: Permission[]) => boolean;
  /** Returns true if any permission in the list is granted. */
  hasAny: (permissions: Permission[]) => boolean;
  /** True while the auth context is still hydrating the session. */
  isLoading: boolean;
}

/**
 * Returns the multi-permission API. Prefer this when a component needs
 * to test more than one permission key in the same render.
 */
export function usePermissions(): UsePermissionsApi {
  const { hasPermission, isLoading } = useAuth();
  return useMemo<UsePermissionsApi>(
    () => ({
      hasPermission,
      hasAll: (perms) => perms.every((p) => hasPermission(p)),
      hasAny: (perms) => perms.some((p) => hasPermission(p)),
      isLoading,
    }),
    [hasPermission, isLoading],
  );
}
